import { ValueGetterParams } from "ag-grid-community";
import { BULK_IDENTIFIER } from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/helpers";
import { Item } from "constants/product-collection";
import { AllocationBagType, getAllocationBagValue, forEachAllocationBag } from "helpers/allocation-dimension";
import { get } from "lodash";
import { generateDoorMap, getMetricValuesFromNewBagData, getMetricValuesFromOldBagData } from "./formula-helpers";

/**
 //  * Formulas for Metrics
 //  *
 //  * @param factoryOptions
 //  *  - provided to `metricsValueGetterCreator`
 //  *  - untyped, easily allow for custom inputs needed for calculation
 //  * @param getterParams
 //  *  - default getterParams from AgGrid
 //  *  - has context and data etc to facilitate calculation
 //  * @returns
 //  *  - a numerical caculated metrics value
 //  */
export const PLAN_ROWS = ({ factoryOptions, getterParams }) => {
    const { data, context }: ValueGetterParams = getterParams;
    const { allocationBagRef } = context;
    const { deliveryId, doors, useSize } = factoryOptions;
    const doorsMap = generateDoorMap(doors);
    let foundDoorWithUnits = false;

    /**
     * New allocation bag
     */
    if (allocationBagRef?.allocationBag) {
        const itemId = get(data, Item.FIELDS.ID);
        const allocationBag = allocationBagRef?.allocationBag;

        if (deliveryId && deliveryId !== "") {
            for (const door of doors) {
                let bulkCount = 0;
                if (useSize) {
                    const { size } = data;
                    bulkCount = getAllocationBagValue(AllocationBagType.allocations, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                        size,
                    ]);
                } else {
                    bulkCount = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                    ]);
                }

                if (bulkCount > 0) {
                    foundDoorWithUnits = true;
                    break;
                }
            }
        } else {
            if (useSize) {
                const { size } = data;
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.allocations, (dimensions, value) => {
                    const { shouldSkip, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        useSize,
                        size
                    );

                    if (shouldSkip) {
                        return;
                    }

                    if (bulkCount > 0) {
                        foundDoorWithUnits = true;
                    }
                });
            } else {
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.bulks, (dimensions, value) => {
                    const { shouldSkip, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        false
                    );

                    if (shouldSkip) {
                        return;
                    }

                    if (bulkCount > 0) {
                        foundDoorWithUnits = true;
                    }
                });
            }
        }
    } else {
        /**
         * Old allocation bag
         */
        let cols: string[][] = [];
        let size: string = undefined;
        if (useSize && data.allocations && data.allocations.current && data.type !== BULK_IDENTIFIER) {
            size = data.size;
            if (size) {
                cols = data.allocations.current.r.map((r) => r.c);
            }
        } else if (data.bulks && data.bulks.r) {
            cols = data.bulks.r.map((r) => r.c);
        }

        for (const col of cols) {
            const { bulkCount, shouldSkip } = getMetricValuesFromOldBagData(deliveryId, doorsMap, col, useSize, size);

            if (shouldSkip) {
                continue;
            }

            if (bulkCount > 0) {
                foundDoorWithUnits = true;
                break;
            }
        }
    }

    return foundDoorWithUnits ? 1 : 0;
};
